import { Activity, ArrowUpRight, Hash, Box } from "lucide-react";
import { fmtNum } from "@/lib/verizonSalesApi";
import { quotaFor } from "@/lib/vzQuotas";

const ratio = (a, b) => (b ? (a || 0) / b : null);
export const pctTxt = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

const totalsOf = (scope, summaryTotals) =>
  scope.length === 1 ? { ...scope[0].metrics, goal_upgrades: scope[0].goal_upgrades } : summaryTotals || {};

// Dashboard / Dealer Sales callout cards for a set of stores. Each card: value, ring (cur vs quota), details, track value.
export const buildCards = ({ scope, summaryTotals, qs, live = true, mr = false }) => {
  const t = totalsOf(scope, summaryTotals);
  const single = scope.length === 1 ? scope[0] : null;
  const st = summaryTotals || {};
  const pf = live && st.current_acts ? st.pacing_acts / st.current_acts : live ? null : 1;
  const q = (key, fb) => quotaFor(qs, key, scope, fb);
  const paced = (v) => (pf && v != null ? v * pf : null);
  const wAvg = (pctKey, wKey) => {
    const rows = scope.filter((x) => x.metrics[pctKey] != null);
    const w = rows.reduce((a, x) => a + (x.metrics[wKey] || 0), 0);
    return w ? rows.reduce((a, x) => a + x.metrics[pctKey] * (x.metrics[wKey] || 0), 0) / w : null;
  };
  const mtd = (s) => (live ? `MTD ${s}` : s);
  const withLine = (c) => ({ line: [["Current", c.fmt(c.cur)], ...(c.extra || []), [c.quotaLabel || "Quota", c.fmt(c.quota)]], ...c });
  const count = (o) => withLine({ kind: "count", fmt: fmtNum, ringCur: o.cur, ringQuota: o.quota, track: o.pace ? paced(o.cur) : o.cur, ...o });
  const rate = (o) => withLine({ kind: "rate", fmt: pctTxt, value: pctTxt(o.cur), ringCur: o.cur, ringQuota: o.quota, track: o.cur, ...o });

  const actsQ = q("activations", t.current_quota);
  const upQ = q("upgrades", t.goal_upgrades);
  const edgeQ = q("edge", null);
  const byodQ = q("byod", t.current_acts);
  const byodPct = single ? single.metrics.current_byod_pct : ratio(t.current_byod, t.current_acts);
  const tabletQ = q("tablets", null);
  const fwaQ = q("fwa", null);
  const twpPlus = ratio(t.current_twp_plus_adds, t.current_twp_acts);
  const twp2 = ratio(t.current_twp_2mr, t.current_twp_2mr_acts);
  const twp3 = ratio(t.current_twp_3mr, t.current_twp_3mr_acts);
  const twpQ = q("twp_plus", 0.7), twp2Q = q("twp_2mr", 0.8), twp3Q = q("twp_3mr", 0.6), autopayQ = q("autopay", 0.3);
  const autopay = ratio(t.current_autopay_ta, t.current_autopay_opp);
  const rewardsCur = single ? single.metrics.current_rewards_pct : wAvg("current_rewards_pct", "current_acts");
  const rewardsPrior = single ? single.metrics.prior_rewards_pct : wAvg("prior_rewards_pct", "prior_acts");
  const zuluCur = ratio(t.current_zulu, t.current_zulu_acts);
  const zuluPrior = ratio(t.prior_zulu, t.prior_zulu_acts);
  const pacingTopups = paced(t.current_topups);
  const topupsQ = q("topups", t.prior_topups);
  const rewardsQ = q("rewards", rewardsPrior);
  const zuluQ = q("zulu", zuluPrior);

  const row1 = [
    count({ key: "acts", title: mtd("Activations"), icon: Activity, value: fmtNum(t.current_acts), cur: t.current_acts, quota: actsQ, pace: true, details: [
      ["Current Activations", fmtNum(t.current_acts)], ["Pacing Activations", fmtNum(t.pacing_acts)], ["Current Quota", fmtNum(actsQ)]] }),
    count({ key: "upgrades", title: mtd("Upgrades"), icon: ArrowUpRight, value: fmtNum(t.current_upgrades), cur: t.current_upgrades, quota: upQ, pace: true, details: [
      ["Current Upgrades", fmtNum(t.current_upgrades)], ["Pacing Upgrades", fmtNum(paced(t.current_upgrades))], ["Upgrade Goal", fmtNum(upQ)]] }),
    count({ key: "edge", title: mtd("Edge"), icon: Hash, value: fmtNum(t.current_edge_acts), cur: t.current_edge_acts, quota: edgeQ, pace: true,
      extra: [["Apply", fmtNum(t.current_edge_apply)]],
      detailTable: { cols: ["Approve", "Apply", "Mix", "Quota"], rows: [{ id: "edge", values: [
        ["Current Edge Approve", fmtNum(t.current_edge_approve)], ["Current Edge Apply", fmtNum(t.current_edge_apply)],
        ["Edge Mix", t.current_acts ? `${(((t.current_edge_acts || 0) / t.current_acts) * 100).toFixed(1)}%` : "—"],
        ["Current Edge Quota", fmtNum(edgeQ)]] }] },
      details: [
      ["Current Edge Approve", fmtNum(t.current_edge_approve), "Current Edge Apply", fmtNum(t.current_edge_apply)],
      ["Edge Mix", t.current_acts ? `${(((t.current_edge_acts || 0) / t.current_acts) * 100).toFixed(1)}%` : "—"],
      ["Current Edge Quota", fmtNum(edgeQ)]] }),
    count({ key: "byod", title: mtd("BYOD"), icon: Box, value: fmtNum(t.current_byod), cur: t.current_byod, quota: byodQ, details: [
      ["Current BYOD", fmtNum(t.current_byod)], ["Pacing BYOD", fmtNum(paced(t.current_byod))], ["BYOD Sales %", pctTxt(byodPct)]] }),
  ];
  const row2 = [
    count({ key: "tablets", title: "Tablets", icon: Box, value: fmtNum(t.current_tablets), cur: t.current_tablets, quota: tabletQ, pace: true,
      ringCur: t.current_tablets, ringQuota: t.current_acts, details: [
      ["Current Tablets", fmtNum(t.current_tablets)], ["Current Tablet %", pctTxt(ratio(t.current_tablets, t.current_acts))], ["Tablets Quota", fmtNum(tabletQ)]] }),
    count({ key: "fwa", title: "Home Internet (FWA)", icon: Box, value: fmtNum(t.current_fwa_acts), cur: t.current_fwa_acts, quota: fwaQ, pace: true, details: [
      ["Current FWA", fmtNum(t.current_fwa_acts)], ["Pacing FWA", fmtNum(t.pacing_fwa_acts ?? paced(t.current_fwa_acts))], ["FWA Quota", fmtNum(fwaQ)]] }),
    rate({ key: "twp", title: "TWP+", icon: Box, cur: twpPlus, quota: twpQ,
      extra: [["2MR", pctTxt(twp2)], ["3MR", pctTxt(twp3)]],
      detailTable: { cols: ["", "Current", "Quota"], rows: [
        { id: "plus", label: "TWP+", values: [["Current TWP+ %", pctTxt(twpPlus)], ["TWP+ Quota", pctTxt(twpQ)]] },
        { id: "2mr", label: "2MR", values: [["Current TWP 2MR %", pctTxt(twp2)], ["2MR Quota", pctTxt(twp2Q)]] },
        { id: "3mr", label: "3MR", values: [["Current TWP 3MR %", pctTxt(twp3)], ["3MR Quota", pctTxt(twp3Q)]] }] },
      details: [
      ["Current TWP+ %", pctTxt(twpPlus), "TWP+ Quota", pctTxt(twpQ)],
      ["Current TWP 2MR %", pctTxt(twp2), "2MR Quota", pctTxt(twp2Q)],
      ["Current TWP 3MR %", pctTxt(twp3), "3MR Quota", pctTxt(twp3Q)]] }),
    rate({ key: "autopay", title: "AutoPay", icon: Box, cur: autopay, quota: autopayQ, details: [
      ["Current AutoPay TA %", pctTxt(autopay)], ["AutoPay Opportunities", fmtNum(t.current_autopay_opp)], ["AutoPay Quota", pctTxt(autopayQ)]] }),
  ];
  const topups = count({ key: "topups", title: "Top-Ups", icon: Box, value: fmtNum(t.current_topups), cur: t.current_topups, quota: topupsQ,
    ringCur: pacingTopups, track: pacingTopups, details: [
    ["Current Top-Ups", fmtNum(t.current_topups)], ["Pacing Top-Ups", fmtNum(pacingTopups)], ["Prior Month Top-Ups", fmtNum(t.prior_topups)],
    ...(topupsQ !== t.prior_topups ? [["Top-Ups Quota", fmtNum(topupsQ)]] : [])] });
  const rest = [
    { ...count({ key: "bob", title: "Bob Calls", icon: Box, value: fmtNum(t.bob_calls), cur: t.bob_calls, quota: null, noRing: true,
      details: [["Bob Calls", fmtNum(t.bob_calls)]] }), line: [["Current", fmtNum(t.bob_calls)]] },
    rate({ key: "rewards", title: "Rewards", icon: Box, cur: rewardsCur, quota: rewardsQ,
      extra: [["Prior", pctTxt(rewardsPrior)]], details: [
      ["Current Rewards %", pctTxt(rewardsCur)], ["Prior Rewards %", pctTxt(rewardsPrior)],
      ...(rewardsQ !== rewardsPrior ? [["Rewards Quota", pctTxt(rewardsQ)]] : [])] }),
    rate({ key: "zulu", title: "Zulu", icon: Box, cur: zuluCur, quota: zuluQ,
      extra: [["Prior", pctTxt(zuluPrior)]], details: [
      ["Current Zulu", fmtNum(t.current_zulu)], ["Zulu Acts", fmtNum(t.current_zulu_acts)], ["Prior Zulu %", pctTxt(zuluPrior)],
      ...(zuluQ !== zuluPrior ? [["Zulu Quota", pctTxt(zuluQ)]] : [])] }),
  ];
  const mrCards = !mr ? [] : [2, 3, 4, 5, 6, 7].map((n) => {
    const cur = ratio(t[`current_mr${n}_pay`], t[`current_mr${n}_acts`]);
    const prior = ratio(t[`prior_mr${n}_pay`], t[`prior_mr${n}_acts`]);
    const quota = q(`mr${n}`, prior);
    return rate({ key: `mr${n}`, title: live ? `Current ${n}MR%` : `${n}MR%`, icon: Box, cur, quota, details: [
      [`${n}MR Payments`, fmtNum(t[`current_mr${n}_pay`]), `${n}MR Acts`, fmtNum(t[`current_mr${n}_acts`])],
      [`Prior ${n}MR %`, pctTxt(prior)], ...(quota !== prior ? [[`${n}MR Quota`, pctTxt(quota)]] : [])] });
  });
  const lifeQ = q("lifeline", t.prior_lifeline);
  const csatCur = ratio(t.current_csat_promoter, t.current_csat_surveys);
  const csatPrior = ratio(t.prior_csat_promoter, t.prior_csat_surveys);
  const csatQ = q("csat", csatPrior);
  const extraCards = !mr ? [] : [
    count({ key: "lifeline", title: "Lifeline Enrollment", icon: Box, value: fmtNum(t.current_lifeline), cur: t.current_lifeline,
      quota: lifeQ, pace: true, quotaLabel: lifeQ === t.prior_lifeline ? "Prior" : "Quota",
      extra: lifeQ === t.prior_lifeline ? [] : [["Prior", fmtNum(t.prior_lifeline)]] }),
    { ...rate({ key: "csat", title: "CSAT Survey", icon: Box, cur: csatCur, quota: csatQ }),
      line: [["Surveys", fmtNum(t.current_csat_surveys)], ["Promo", fmtNum(t.current_csat_promoter)],
        ["Detract", fmtNum(t.current_csat_detractors)], ["Score", pctTxt(csatCur)]] },
  ];
  return { row1, row2, row3: [topups, ...mrCards, ...rest, ...extraCards] };
};
